// The tiny UI layer (architecture §9.7): `h(tag, props, ...children)` builds DOM once, and
// `signal()` is a minimal observable store that screens update through. No framework, no virtual
// DOM.
//
// Implemented fully (contracts step). Consumers: settings/store.ts, every ui/** screen, app.ts.
// `signal`, `computed` and `effect` are DOM-free (usable in Node tests); `h` needs `document`.

export type Unsubscribe = () => void;

export interface ReadonlySignal<T> {
  get(): T;
  /** Calls `fn(value, previous)` after every change (not immediately). */
  subscribe(fn: (value: T, previous: T) => void): Unsubscribe;
}

export interface Signal<T> extends ReadonlySignal<T> {
  /** Sets the value; subscribers run only if it differs (by the signal's `equals`). */
  set(value: T): void;
  update(fn: (value: T) => T): void;
}

const SIGNAL = Symbol('ballio.signal');

/** A writable signal. `equals` decides whether a `set` is a change (default `Object.is`). */
export function signal<T>(initial: T, equals: (a: T, b: T) => boolean = Object.is): Signal<T> {
  let value = initial;
  const subs = new Set<(value: T, previous: T) => void>();
  const s: Signal<T> & { [SIGNAL]: true } = {
    [SIGNAL]: true,
    get: () => value,
    set(next: T) {
      if (equals(value, next)) return;
      const prev = value;
      value = next;
      for (const fn of [...subs]) fn(value, prev);
    },
    update(fn: (value: T) => T) {
      s.set(fn(value));
    },
    subscribe(fn) {
      subs.add(fn);
      return () => void subs.delete(fn);
    },
  };
  return s;
}

/** A read-only signal recomputed from `fn` whenever any of `sources` changes. */
export function computed<T>(
  sources: readonly ReadonlySignal<unknown>[],
  fn: () => T,
  equals: (a: T, b: T) => boolean = Object.is,
): ReadonlySignal<T> {
  const out = signal(fn(), equals);
  for (const src of sources) src.subscribe(() => out.set(fn()));
  return { get: out.get, subscribe: out.subscribe, [SIGNAL]: true } as ReadonlySignal<T>;
}

/** Runs `fn` now and again after any of `sources` changes. Returns a disposer. */
export function effect(sources: readonly ReadonlySignal<unknown>[], fn: () => void): Unsubscribe {
  fn();
  const offs = sources.map((src) => src.subscribe(() => fn()));
  return () => {
    for (const off of offs) off();
  };
}

export function isSignal(v: unknown): v is ReadonlySignal<unknown> {
  return typeof v === 'object' && v !== null && SIGNAL in v;
}

// ---------------------------------------------------------------------------------------------
// h()
// ---------------------------------------------------------------------------------------------

type Primitive = string | number;

/** A child: nodes, text, nothing, signals (bound text) or nested arrays of those. */
export type Child =
  Node | Primitive | boolean | null | undefined | ReadonlySignal<Primitive> | readonly Child[];

/**
 * Element props. Conventions:
 * - `class` / `className`: string (or a signal of one).
 * - `style`: a CSS text string, or an object of properties (`--vars` allowed).
 * - `dataset`: `{ key: value }` → `data-key="value"`.
 * - `onClick`, `onKeydown`, …: event listeners (`on` + event name, case-insensitive).
 * - `ref`: called with the element after it is built.
 * - anything else: set as a property when the element has it (`value`, `checked`, `disabled`,
 *   `textContent`, `htmlFor`, …), otherwise as an attribute (`aria-*`, `role`, `for`, …).
 *   `true` → empty attribute, `false`/null/undefined → removed. Any value may be a signal: it is
 *   applied now and on every change.
 */
export type Props = Record<string, unknown>;

/** Keys whose element property is read-only, so they must be set as attributes. */
const ATTRIBUTE_ONLY = new Set(['list', 'form']);

function applyProp(el: HTMLElement, key: string, value: unknown): void {
  if (key === 'class' || key === 'className') {
    el.className = value == null || value === false ? '' : String(value);
  } else if (key === 'style') {
    if (typeof value === 'string') el.style.cssText = value;
    else if (value && typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (v == null || v === false) el.style.removeProperty(k);
        else el.style.setProperty(k.startsWith('--') ? k : toKebab(k), String(v));
      }
    }
  } else if (key in el && !key.includes('-') && !ATTRIBUTE_ONLY.has(key)) {
    (el as unknown as Record<string, unknown>)[key] = value;
  } else if (value === true) {
    el.setAttribute(key, '');
  } else if (value === false || value == null) {
    el.removeAttribute(key);
  } else {
    el.setAttribute(key, String(value));
  }
}

function toKebab(k: string): string {
  return k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
}

/** Appends children (flattening arrays, binding signals as text). */
export function append(parent: Node, ...children: Child[]): void {
  for (const child of children) {
    if (child == null || child === false || child === true) continue;
    if (Array.isArray(child)) append(parent, ...(child as readonly Child[]));
    else if (isSignal(child)) {
      const sig = child as ReadonlySignal<Primitive>;
      const text = document.createTextNode(String(sig.get()));
      sig.subscribe((v) => {
        text.data = String(v);
      });
      parent.appendChild(text);
    } else if (typeof child === 'string' || typeof child === 'number') {
      parent.appendChild(document.createTextNode(String(child)));
    } else parent.appendChild(child as Node);
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | null,
  ...children: Child[]
): HTMLElementTagNameMap[K];
export function h(tag: string, props?: Props | null, ...children: Child[]): HTMLElement;
export function h(tag: string, props?: Props | null, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag);
  let ref: ((el: HTMLElement) => void) | null = null;
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (key === 'ref') {
        if (typeof value === 'function') ref = value as (el: HTMLElement) => void;
      } else if (key === 'dataset') {
        for (const [k, v] of Object.entries((value ?? {}) as Record<string, unknown>)) {
          if (v != null) el.dataset[k] = String(v);
        }
      } else if (key.length > 2 && key.startsWith('on') && typeof value === 'function') {
        el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      } else if (isSignal(value)) {
        applyProp(el, key, value.get());
        value.subscribe((v) => applyProp(el, key, v));
      } else {
        applyProp(el, key, value);
      }
    }
  }
  append(el, ...children);
  if (ref) ref(el);
  return el;
}

/** Removes every child of `el`. */
export function clear(el: Node): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Shows or hides an element with the `hidden` attribute. */
export function show(el: HTMLElement, visible: boolean): void {
  el.hidden = !visible;
}
